import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import chromatism from 'chromatism';
import { useIntl } from 'react-intl-next';
import { Grid, Inline } from '@atlaskit/primitives/compiled';
import { useThemeObserver } from '@atlaskit/tokens';
import { Color } from './Color';
import getColorMessage from './getColorMessage';
import { DEFAULT_COLOR_PICKER_COLUMNS, getColorsPerRowFromPalette, getSelectedRowAndColumnFromPalette, getTokenCSSVariableValue } from './utils';

/**
 * For a given color pick the color from a list of colors with
 * the highest contrast
 *
 * @param color color string, supports HEX, RGB, RGBA etc.
 * @param useIconToken boolean, describes if a token should be used for the icon color
 * @returns Highest contrast color in pool
 */
function getCheckMarkColor(color, useIconToken) {
  var tokenVal = getTokenCSSVariableValue(color);
  var colorValue = !!tokenVal ? tokenVal : color;

  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  var contrastColor = ['#FFFFFF', '#42526E'].sort(function (a, b) {
    return chromatism.difference(b, colorValue) - chromatism.difference(a, colorValue);
  })[0];
  if (!useIconToken) {
    return contrastColor;
  }

  // Use of these token comes from guidance from designers in the Design System team
  // they are only intended for use with text colors (and there are different tokens
  // planned to be used when this extended to be used with other palettes).
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
  return contrastColor === '#FFFFFF' ? "var(--ds-icon-inverse, #FFFFFF)" : "var(--ds-icon, #44546F)";
}

/**
 * ColorPalette component for displaying a grid of selectable colors
 *
 * Features:
 * - Responsive grid layout
 * - Keyboard navigation support
 * - Accessibility compliance (ARIA attributes)
 * - Theme-aware tooltips
 * - Design token integration
 * - Customizable color mapping
 */
var ColorPalette = function ColorPalette(_ref) {
  var _ref$cols = _ref.cols,
    cols = _ref$cols === void 0 ? DEFAULT_COLOR_PICKER_COLUMNS : _ref$cols,
    onClick = _ref.onClick,
    onKeyDown = _ref.onKeyDown,
    selectedColor = _ref.selectedColor,
    paletteOptions = _ref.paletteOptions;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var palette = paletteOptions.palette,
    hexToPaletteColor = paletteOptions.hexToPaletteColor,
    paletteColorTooltipMessages = paletteOptions.paletteColorTooltipMessages;
  var _useThemeObserver = useThemeObserver(),
    tokenTheme = _useThemeObserver.colorMode;
  var useIconToken = !!hexToPaletteColor;

  // Refs for keyboard navigation
  var paletteRef = useRef(null);
  var currentFocusRef = useRef({
    row: 0,
    col: 0
  });
  var colorsPerRow = useMemo(function () {
    return getColorsPerRowFromPalette(palette, cols);
  }, [palette, cols]);

  // Get initial focus position based on selected color
  var _useMemo = useMemo(function () {
      return getSelectedRowAndColumnFromPalette(palette, selectedColor, cols);
    }, [palette, selectedColor, cols]),
    selectedRowIndex = _useMemo.selectedRowIndex,
    selectedColumnIndex = _useMemo.selectedColumnIndex;

  // Focus management utility
  var focusColorAt = useCallback(function (row, col) {
    var _rowElement$children$, _rowElement$children$2;
    if (!paletteRef.current) {
      return;
    }
    var rowElement = paletteRef.current.children[row];
    if (!(rowElement instanceof HTMLElement)) {
      return;
    }
    var colorButtonCandidate = (_rowElement$children$ = rowElement.children[col]) === null || _rowElement$children$ === void 0 || (_rowElement$children$2 = _rowElement$children$.querySelector) === null || _rowElement$children$2 === void 0 ? void 0 : _rowElement$children$2.call(_rowElement$children$, 'button');
    var colorButton = colorButtonCandidate instanceof HTMLButtonElement ? colorButtonCandidate : null;
    if (colorButton) {
      colorButton.focus();
      currentFocusRef.current = {
        row: row,
        col: col
      };
    }
  }, []);

  // Initialize focus position and handle autofocus
  useEffect(function () {
    if (selectedRowIndex >= 0 && selectedColumnIndex >= 0) {
      currentFocusRef.current = {
        row: selectedRowIndex,
        col: selectedColumnIndex
      };
    } else {
      currentFocusRef.current = {
        row: 0,
        col: 0
      };
    }
  }, [selectedRowIndex, selectedColumnIndex]);

  // Keyboard navigation handler
  var handleKeyDown = useCallback(function (value, label, event) {
    var _colorsPerRow$row;
    var _currentFocusRef$curr = currentFocusRef.current,
      row = _currentFocusRef$curr.row,
      col = _currentFocusRef$curr.col;
    var maxRow = colorsPerRow.length - 1;
    var maxCol = ((_colorsPerRow$row = colorsPerRow[row]) === null || _colorsPerRow$row === void 0 ? void 0 : _colorsPerRow$row.length) - 1 || 0;
    switch (event.key) {
      case 'ArrowRight':
        {
          event.preventDefault();
          if (col < maxCol) {
            focusColorAt(row, col + 1);
          } else if (row < maxRow) {
            // Move to first color of next row
            focusColorAt(row + 1, 0);
          } else {
            // Wrap to first color of first row
            focusColorAt(0, 0);
          }
          break;
        }
      case 'ArrowLeft':
        {
          event.preventDefault();
          if (col > 0) {
            focusColorAt(row, col - 1);
          } else if (row > 0) {
            var _colorsPerRow;
            // Move to last color of previous row
            var prevRowMaxCol = ((_colorsPerRow = colorsPerRow[row - 1]) === null || _colorsPerRow === void 0 ? void 0 : _colorsPerRow.length) - 1 || 0;
            focusColorAt(row - 1, prevRowMaxCol);
          } else {
            var _colorsPerRow$maxRow;
            // Wrap to last color of last row
            var lastRowMaxCol = ((_colorsPerRow$maxRow = colorsPerRow[maxRow]) === null || _colorsPerRow$maxRow === void 0 ? void 0 : _colorsPerRow$maxRow.length) - 1 || 0;
            focusColorAt(maxRow, lastRowMaxCol);
          }
          break;
        }
      case 'ArrowDown':
        {
          event.preventDefault();
          if (row < maxRow) {
            var _colorsPerRow2;
            // Move to same column in next row, or last available column
            var nextRowMaxCol = ((_colorsPerRow2 = colorsPerRow[row + 1]) === null || _colorsPerRow2 === void 0 ? void 0 : _colorsPerRow2.length) - 1 || 0;
            var targetCol = Math.min(col, nextRowMaxCol);
            focusColorAt(row + 1, targetCol);
          } else {
            var _colorsPerRow$;
            // Wrap to same column in first row
            var firstRowMaxCol = ((_colorsPerRow$ = colorsPerRow[0]) === null || _colorsPerRow$ === void 0 ? void 0 : _colorsPerRow$.length) - 1 || 0;
            var _targetCol = Math.min(col, firstRowMaxCol);
            focusColorAt(0, _targetCol);
          }
          break;
        }
      case 'ArrowUp':
        {
          event.preventDefault();
          if (row > 0) {
            var _colorsPerRow3;
            // Move to same column in previous row, or last available column
            var _prevRowMaxCol = ((_colorsPerRow3 = colorsPerRow[row - 1]) === null || _colorsPerRow3 === void 0 ? void 0 : _colorsPerRow3.length) - 1 || 0;
            var _targetCol2 = Math.min(col, _prevRowMaxCol);
            focusColorAt(row - 1, _targetCol2);
          } else {
            var _colorsPerRow$maxRow2;
            // Wrap to same column in last row
            var _lastRowMaxCol = ((_colorsPerRow$maxRow2 = colorsPerRow[maxRow]) === null || _colorsPerRow$maxRow2 === void 0 ? void 0 : _colorsPerRow$maxRow2.length) - 1 || 0;
            var _targetCol3 = Math.min(col, _lastRowMaxCol);
            focusColorAt(maxRow, _targetCol3);
          }
          break;
        }
      case 'Home':
        {
          event.preventDefault();
          focusColorAt(row, 0);
          break;
        }
      case 'End':
        {
          event.preventDefault();
          focusColorAt(row, maxCol);
          break;
        }
      case 'PageUp':
        {
          event.preventDefault();
          focusColorAt(0, col);
          break;
        }
      case 'PageDown':
        {
          var _colorsPerRow$maxRow3;
          event.preventDefault();
          var _lastRowMaxCol2 = ((_colorsPerRow$maxRow3 = colorsPerRow[maxRow]) === null || _colorsPerRow$maxRow3 === void 0 ? void 0 : _colorsPerRow$maxRow3.length) - 1 || 0;
          var _targetCol4 = Math.min(col, _lastRowMaxCol2);
          focusColorAt(maxRow, _targetCol4);
          break;
        }
      case 'Tab':
        {
          // Allow Tab to move to next focusable element (don't prevent default)
          // This will allow Tab to move between color palettes
          if (onKeyDown) {
            onKeyDown(value, label, event);
          }
          break;
        }
      case 'Enter':
      case ' ':
        {
          event.preventDefault();
          onClick(value, label);
          break;
        }
      default:
        {
          // Pass through to custom onKeyDown handler if provided
          if (onKeyDown) {
            onKeyDown(value, label, event);
          }
          break;
        }
    }
  }, [colorsPerRow, focusColorAt, onClick, onKeyDown]);
  return /*#__PURE__*/React.createElement(Grid, {
    gap: "space.050",
    ref: paletteRef,
    role: "group"
  }, colorsPerRow.map(function (row, rowIndex) {
    return /*#__PURE__*/React.createElement(Inline, {
      rowSpace: "space.050",
      key: "row-first-color-".concat(row[0].value),
      role: "radiogroup"
    }, row.map(function (_ref2, colIndex) {
      var value = _ref2.value,
        label = _ref2.label,
        border = _ref2.border,
        message = _ref2.message,
        decorator = _ref2.decorator;
      var tooltipMessage = message;

      // Override with theme-specific tooltip messages if provided
      if (paletteColorTooltipMessages) {
        if (tokenTheme === 'dark') {
          tooltipMessage = getColorMessage(paletteColorTooltipMessages.dark, value.toUpperCase());
        }
        if (tokenTheme === 'light') {
          tooltipMessage = getColorMessage(paletteColorTooltipMessages.light, value.toUpperCase());
        }
      }

      // Determine if this color should be focusable
      var isSelectedColor = value === selectedColor;
      var isFirstColor = rowIndex === 0 && colIndex === 0;

      // Only the selected color or first color should be focusable via Tab
      // This allows Tab to move between color palettes
      var shouldBeFocusable = isSelectedColor || !selectedColor && isFirstColor;
      return /*#__PURE__*/React.createElement(Color, {
        key: value,
        value: value,
        borderColor: border,
        label: tooltipMessage ? formatMessage(tooltipMessage) : label,
        onClick: onClick,
        onKeyDown: handleKeyDown,
        isSelected: isSelectedColor,
        checkMarkColor: getCheckMarkColor(value, useIconToken),
        hexToPaletteColor: hexToPaletteColor,
        decorator: decorator,
        tabIndex: shouldBeFocusable ? 0 : -1
      });
    }));
  }));
};
export default ColorPalette;